import { BuildingOffice2Icon, CalendarDaysIcon, ChartBarIcon, ChatBubbleLeftIcon, ChatBubbleLeftRightIcon, ClipboardDocumentListIcon, FlagIcon, HeartIcon, RectangleGroupIcon, ShareIcon, StarIcon, UsersIcon } from "@heroicons/react/24/outline";
import { PERFORMANCE_CYCLE_DETAILS_ACCESS } from "@packages/core/access/settingsAccess.ts";
import { withFeature } from "@packages/core/access/featureAccess.ts";
import { hasFeature } from "@packages/core/session/features.ts";
import { hasProduct } from "@packages/core/session/products.ts";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { getCommandMenuLinksRegistryPlugin } from "@packages/core-client/registration/command-menu/CommandMenuLinksPlugin.ts";
import { registerCreateMenuItem } from "@packages/core-client/registration/createMenu.tsx";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import { initActionItemsI18next } from "@domains/action-items/client/i18n/i18next.ts";
import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { initEngagementI18next } from "@domains/engagement/client/i18n/i18next.ts";
import { initFeedbackI18next } from "@domains/feedback/client/i18n/i18next.ts";
import { RECOGNITION_LOCATIONS } from "@domains/good-vibes/client/components/goodVibesTracking.ts";
import { ORG_CHART_ACCESS } from "@domains/org-chart/shared/orgChartAccess.ts";
import { initMeetingsI18next } from "@domains/meetings/client/i18n/i18next.ts";
import { initPlansI18next } from "@domains/plans/client/i18n/i18next.ts";
import { REVIEW_GOOD_VIBE_SEGMENT } from "@domains/reviews/client/components/reviewRoutes.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import { ORGANIZATION_FEEDBACK_INBOX_ACCESS, ORGANIZATION_ORG_CHART_EDIT_ACCESS, ORGANIZATION_PLANS_ACCESS } from "#shared/access.ts";
import { initI18next } from "./i18n/i18next.ts";
import { PEOPLE_REVIEW_PLACEMENT, type ReviewPlacement, resolveReviewPlacement } from "./pages/directory/profile/reviews/reviewPlacement.ts";
import { meShortcutLinks } from "./meShortcutLinks.ts";
import { reportOrgChartRenderFailed } from "./pages/org-chart/orgChartTelemetry.ts";

function registerProfileRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const profileArea = registerArea(runtime, "person-profile", "/org/people/:personId", UsersIcon, i18nextInstance);

    const profileTabs = profileArea.registerTabsArea("main", {
        hideTitle: true,
        commandMenu: false,
        chrome: async () => (await import("./pages/directory/profile/profileTabsChrome.tsx")).PROFILE_TABS_CHROME
    });

    const about = profileTabs.registerRoute("about", {
        index: true,
        page: "DirectoryProfileAbout",
        lazy: async () => {
            const { ProfileAboutPage } = await import("./pages/directory/profile/about/ProfileAboutPage.tsx");

            return { element: <ProfileAboutPage /> };
        }
    });

    const goals = profileTabs.registerRoute("goals", {
        path: "goals",
        page: "DirectoryProfileGoals",
        access: withFeature("Goals"),
        lazy: async () => {
            const { ProfileGoalsPage } = await import("./pages/directory/profile/goals/ProfileGoalsPage.tsx");

            return { element: <ProfileGoalsPage /> };
        }
    });

    goals.registerOverlay("progress-update", { segment: "update/:goalId", page: "DirectoryProfileGoalsProgressUpdate", lazy: loadGoalProgressUpdateRoute });

    const reviews = profileTabs.registerRoute("reviews", {
        path: "reviews",
        page: "DirectoryProfileReviews",
        access: withFeature("PerformanceReviews"),
        lazy: async () => {
            const { ProfileReviewsPage } = await import("./pages/directory/profile/reviews/ProfileReviewsPage.tsx");

            return { element: <ProfileReviewsPage /> };
        }
    });

    profileTabs.registerRoute("timeline-redirect", {
        path: "timeline/*",
        lazy: async () => {
            const { ProfileTimelineRedirect } = await import("./pages/directory/profile/ProfileTimelineRedirect.tsx");

            return { element: <ProfileTimelineRedirect /> };
        }
    });

    const goodVibes = profileTabs.registerRoute("good-vibes", {
        path: "good-vibes",
        page: "DirectoryProfileGoodVibes",
        access: withFeature("Recognition"),
        lazy: async () => {
            const { KeyedProfileGoodVibesPage } = await import("./pages/directory/profile/good-vibes/ProfileGoodVibesPage.tsx");

            return { element: <KeyedProfileGoodVibesPage /> };
        }
    });

    goodVibes.registerOverlay("detail", {
        segment: ":recognitionId",
        page: "DirectoryProfileGoodVibeDetail",
        lazy: async () => {
            const { GoodVibeDetailRoute } = await import("./pages/directory/profile/good-vibes/GoodVibeDetailRoute.tsx");

            return { element: <GoodVibeDetailRoute trackingLocation={RECOGNITION_LOCATIONS.PeopleGoodVibes} /> };
        }
    });

    const meetings = profileTabs.registerRoute("meetings", {
        path: "meetings",
        page: "DirectoryProfileMeetings",
        access: withFeature("Meetings"),
        lazy: async () => {
            const { KeyedProfileMeetingsPage } = await import("./pages/directory/profile/meetings/ProfileMeetingsPage.tsx");

            return { element: <KeyedProfileMeetingsPage /> };
        }
    });

    const actionItems = profileTabs.registerRoute("action-items", {
        path: "action-items",
        page: "DirectoryProfileActionItems",
        lazy: async () => {
            const { ProfileActionItemsPage } = await import("./pages/directory/profile/action-items/ProfileActionItemsPage.tsx");

            return { element: <ProfileActionItemsPage /> };
        }
    });

    actionItems.registerOverlay("action-item", {
        segment: ":actionItemId",
        page: "DirectoryProfileActionItem",
        lazy: async () => {
            const { ActionItemRoute } = await import("./pages/directory/profile/action-items/ActionItemRoute.tsx");

            return { element: <ActionItemRoute /> };
        }
    });

    const engagement = profileTabs.registerRoute("engagement", {
        path: "engagement",
        page: "DirectoryProfileEngagement",
        lazy: async () => {
            const { ProfileEngagementPage } = await import("./pages/directory/profile/engagement/ProfileEngagementPage.tsx");

            return { element: <ProfileEngagementPage /> };
        }
    });

    engagement.registerOverlay("good-vibe-detail", {
        segment: "good-vibes/:recognitionId",
        page: "DirectoryProfileEngagementGoodVibeDetail",
        lazy: async () => {
            const { EngagementGoodVibeDetailRoute } = await import("./pages/directory/profile/engagement/EngagementGoodVibeDetailRoute.tsx");

            return { element: <EngagementGoodVibeDetailRoute /> };
        }
    });

    const progression = profileTabs.registerRoute("progression", {
        path: "progression",
        page: "DirectoryProfileProgression",
        access: [hasFeature("Goals"), hasFeature("PerformanceReviews"), hasFeature("Plans")],
        lazy: async () => {
            const { ProfileProgressionPage } = await import("./pages/directory/profile/progression/ProfileProgressionPage.tsx");

            return { element: <ProfileProgressionPage /> };
        }
    });

    about.registerLink({ priority: 1000 });
    goodVibes.registerLink({ priority: 998 });
    reviews.registerLink({ priority: 996 });
    goals.registerLink({ priority: 995 });

    return { meetings, actionItems, engagement, progression };
}

function registerReviewRoutes(runtime: FireflyRuntime, i18nextInstance: i18n, placement: ReviewPlacement) {
    const { basePath, access, areaIdPrefix, pageHandlePrefix } = resolveReviewPlacement(placement);

    registerArea(runtime, areaIdPrefix, basePath, StarIcon, i18nextInstance, { access }).registerPage({
        page: `${pageHandlePrefix}Details`,
        lazy: async () => {
            const { ReviewDetailsPage } = await import("./pages/directory/profile/reviews/ReviewDetailsPage.tsx");

            return { element: <ReviewDetailsPage placement={placement} /> };
        }
    });

    registerArea(runtime, `${areaIdPrefix}-share`, `${basePath}/share`, StarIcon, i18nextInstance, { access }).registerPage({
        page: `${pageHandlePrefix}Share`,
        lazy: async () => {
            const { ReviewSharePage } = await import("./pages/directory/profile/reviews/ReviewSharePage.tsx");

            return { element: <ReviewSharePage placement={placement} /> };
        }
    });

    const reviewWriteArea = registerArea(runtime, `${areaIdPrefix}-write`, `${basePath}/:perspective`, StarIcon, i18nextInstance, { access });

    reviewWriteArea.registerPage({
        page: `${pageHandlePrefix}Write`,
        fullHeight: true,
        lazy: async () => {
            const { ReviewWritePage } = await import("./pages/directory/profile/reviews/ReviewWritePage.tsx");

            return { element: <ReviewWritePage placement={placement} /> };
        }
    });

    reviewWriteArea.registerOverlay("good-vibe-detail", {
        segment: `${REVIEW_GOOD_VIBE_SEGMENT}/:recognitionId`,
        page: `${pageHandlePrefix}WriteGoodVibeDetail`,
        lazy: async () => {
            const { ReviewWriteGoodVibeRoute } = await import("./pages/directory/profile/reviews/ReviewWriteGoodVibeRoute.tsx");

            return { element: <ReviewWriteGoodVibeRoute placement={placement} /> };
        }
    });
}

function registerProgressionReviewsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const progressionReviewsArea = registerArea(runtime, "person-progression-reviews", "/org/people/:personId/progression/reviews", StarIcon, i18nextInstance, { access: withFeature("PerformanceReviews") });

    progressionReviewsArea.registerPage({
        page: "DirectoryProfileProgressionReviews",
        lazy: async () => {
            const { ProfileReviewsListPage } = await import("./pages/directory/profile/reviews/ProfileReviewsListPage.tsx");
            const { ProfileErrorBoundary } = await import("./pages/directory/profile/ProfileErrorBoundary.tsx");

            return { element: <ProfileReviewsListPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });
}

function registerMeetingsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const meetingArea = registerArea(runtime, "person-meeting", "/org/people/:personId/meetings/:meetingId", CalendarDaysIcon, i18nextInstance, { access: withFeature("Meetings") });

    meetingArea.registerPage({
        page: "DirectoryMeetingDetail",
        fullHeight: true,
        lazy: async () => {
            const { MeetingDetailPage } = await import("./pages/meetings/detail/MeetingDetailPage.tsx");
            const { MeetingErrorBoundary } = await import("./pages/meetings/detail/MeetingErrorBoundary.tsx");

            return { element: <MeetingDetailPage />, errorElement: <MeetingErrorBoundary /> };
        }
    });

    meetingArea.registerOverlay("action-item", {
        segment: "action-items/:actionItemId",
        page: "DirectoryMeetingActionItem",
        lazy: async () => {
            const { MeetingActionItemRoute } = await import("./pages/meetings/detail/MeetingActionItemRoute.tsx");

            return { element: <MeetingActionItemRoute /> };
        }
    });
}

function registerGoalEditRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalEditArea = registerArea(runtime, "person-goal-edit", "/org/people/:personId/goals/:goalId/edit", FlagIcon, i18nextInstance, { access: withFeature("Goals") });

    goalEditArea.registerPage({
        page: "DirectoryGoalEdit",
        fullHeight: true,
        lazy: async () => {
            const { GoalEditPage } = await import("./pages/goals/edit/GoalEditPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalEditPage />, errorElement: <GoalErrorBoundary /> };
        }
    });
}

async function loadGoalProgressUpdateRoute() {
    const { GoalProgressUpdateRoute } = await import("./pages/goals/detail/GoalProgressUpdateRoute.tsx");

    return { element: <GoalProgressUpdateRoute /> };
}

function registerMemberPlansRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const memberPlansArea = registerArea(runtime, "person-plans", "/org/people/:personId/plans", ClipboardDocumentListIcon, i18nextInstance, { access: withFeature("Plans") });

    memberPlansArea.registerPage({
        page: "DirectoryProfilePlans",
        lazy: async () => {
            const { ProfilePlansPage } = await import("./pages/directory/profile/plans/ProfilePlansPage.tsx");
            const { ProfileErrorBoundary } = await import("./pages/directory/profile/ProfileErrorBoundary.tsx");

            return { element: <ProfilePlansPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });
}

function registerProgressionGoalsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const progressionGoalsArea = registerArea(runtime, "person-progression-goals", "/org/people/:personId/progression/goals", FlagIcon, i18nextInstance, { access: withFeature("Goals") });

    progressionGoalsArea.registerPage({
        page: "DirectoryProfileProgressionGoals",
        lazy: async () => {
            const { ProgressionGoalsListPage } = await import("./pages/directory/profile/progression/goals/ProgressionGoalsListPage.tsx");
            const { ProfileErrorBoundary } = await import("./pages/directory/profile/ProfileErrorBoundary.tsx");

            return { element: <ProgressionGoalsListPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });

    progressionGoalsArea.registerOverlay("progress-update", { segment: "update/:goalId", page: "DirectoryProfileProgressionGoalsProgressUpdate", lazy: loadGoalProgressUpdateRoute });
}

function registerEngagementFeedbackRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const engagementFeedbackArea = registerArea(runtime, "person-engagement-feedback", "/org/people/:personId/engagement/feedback", ChatBubbleLeftRightIcon, i18nextInstance, { access: withFeature("Engagement") });

    engagementFeedbackArea.registerPage({
        page: "DirectoryProfileEngagementFeedback",
        lazy: async () => {
            const { EngagementFeedbackListPage } = await import("./pages/directory/profile/engagement/feedback/EngagementFeedbackListPage.tsx");
            const { ProfileErrorBoundary } = await import("./pages/directory/profile/ProfileErrorBoundary.tsx");

            return { element: <EngagementFeedbackListPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });

    runtime.registerRoute({ path: "/org/people/:personId/engagement/feedback/:feedbackId", element: null }, { parentPath: "/org/people/:personId/engagement/feedback" });
}

function registerEngagementMetricsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const engagementMetricsArea = registerArea(runtime, "person-engagement-metrics", "/org/people/:personId/engagement/metrics", ChartBarIcon, i18nextInstance, { access: withFeature("Engagement") });

    engagementMetricsArea.registerPage({
        page: "DirectoryProfileEngagementMetrics",
        lazy: async () => {
            const { ProfileEngagementMetricsPage } = await import("./pages/directory/profile/engagement/metrics/ProfileEngagementMetricsPage.tsx");
            const { ProfileErrorBoundary } = await import("./pages/directory/profile/ProfileErrorBoundary.tsx");

            return { element: <ProfileEngagementMetricsPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });
}

function registerGoalsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalArea = registerArea(runtime, "person-goal", "/org/people/:personId/goals/:goalId", FlagIcon, i18nextInstance, { access: withFeature("Goals") });

    goalArea.registerPage({
        page: "DirectoryGoalDetail",
        lazy: async () => {
            const { GoalDetailPage } = await import("./pages/goals/detail/GoalDetailPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalDetailPage />, errorElement: <GoalErrorBoundary /> };
        }
    });

    goalArea.registerOverlay("progress-update", { segment: "update", page: "DirectoryGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });
    goalArea.registerOverlay("progress-update-edit", { segment: "update/:updateId", page: "DirectoryGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });
    goalArea.registerOverlay("close", { segment: "close", page: "DirectoryGoalClose", lazy: loadGoalCloseRoute });
}

async function loadGoalCloseRoute() {
    const { GoalCloseRoute } = await import("./pages/goals/detail/GoalCloseRoute.tsx");

    return { element: <GoalCloseRoute /> };
}

function registerOrganizationGoalRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalArea = registerArea(runtime, "organization-goal", "/org/goals/:goalId", FlagIcon, i18nextInstance, { access: withFeature("Goals") });

    goalArea.registerPage({
        page: "OrganizationGoalDetail",
        lazy: async () => {
            const { GoalDetailPage } = await import("./pages/goals/detail/GoalDetailPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalDetailPage />, errorElement: <GoalErrorBoundary /> };
        }
    });

    goalArea.registerOverlay("progress-update", { segment: "update", page: "OrganizationGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });
    goalArea.registerOverlay("progress-update-edit", { segment: "update/:updateId", page: "OrganizationGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });

    const goalEditArea = registerArea(runtime, "organization-goal-edit", "/org/goals/:goalId/edit", FlagIcon, i18nextInstance, { access: withFeature("Goals") });

    goalEditArea.registerPage({
        page: "OrganizationGoalEdit",
        fullHeight: true,
        lazy: async () => {
            const { GoalEditPage } = await import("./pages/goals/edit/GoalEditPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalEditPage />, errorElement: <GoalErrorBoundary /> };
        }
    });
}

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);
    }
}

export const registerOrganization: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initMeetingsI18next(runtime);
    initConnectedAppsI18next(runtime);
    initEngagementI18next(runtime);
    initActionItemsI18next(runtime);
    initFeedbackI18next(runtime);
    initReviewCyclesI18next(runtime);
    initPlansI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "organization", "/org", BuildingOffice2Icon, i18nextInstance);
    const mainPage = area.registerTabsArea("main", { sidebarFlag: "org-sidebar-navigation", sidebarGroups: ["engagement", "progression", "compensation"] });

    const overview = mainPage.registerRoute("overview", {
        index: true,
        page: "OrganizationOverview",
        lazy: async () => {
            const { OrganizationOverviewPage } = await import("./pages/overview/OrganizationOverviewPage.tsx");

            return { element: <OrganizationOverviewPage /> };
        }
    });

    const people = mainPage.registerRoute("people", {
        path: "/people",
        page: "Directory",
        lazy: async () => {
            const { PeopleListPage } = await import("./pages/directory/list/PeopleListPage.tsx");

            return { element: <PeopleListPage /> };
        }
    });

    const goodVibes = mainPage.registerRoute("good-vibes", {
        path: "/good-vibes",
        page: "OrganizationGoodVibes",
        access: withFeature("Recognition"),
        lazy: async () => {
            const { OrganizationGoodVibesPage } = await import("./pages/good-vibes/OrganizationGoodVibesPage.tsx");

            return { element: <OrganizationGoodVibesPage /> };
        }
    });

    goodVibes.registerOverlay("detail", {
        segment: ":recognitionId",
        page: "OrganizationGoodVibeDetail",
        lazy: async () => {
            const { OrganizationGoodVibeDetailRoute } = await import("./pages/good-vibes/OrganizationGoodVibeDetailRoute.tsx");

            return { element: <OrganizationGoodVibeDetailRoute /> };
        }
    });

    const goals = mainPage.registerRoute("goals", {
        path: "/goals",
        page: "OrganizationGoals",
        access: withFeature("Goals"),
        lazy: async () => {
            const { OrganizationGoalsPage } = await import("./pages/goals/list/OrganizationGoalsPage.tsx");

            return { element: <OrganizationGoalsPage /> };
        }
    });

    goals.registerOverlay("progress-update", { segment: "update/:goalId", page: "OrganizationGoalsProgressUpdate", lazy: loadGoalProgressUpdateRoute });

    const plans = mainPage.registerRoute("plans", {
        path: "/plans",
        page: "OrganizationPlans",
        access: withFeature("Plans", ORGANIZATION_PLANS_ACCESS),
        lazy: async () => {
            const { OrganizationPlansPage } = await import("./pages/plans/OrganizationPlansPage.tsx");

            return { element: <OrganizationPlansPage /> };
        }
    });

    const reviewCycles = mainPage.registerRoute("review-cycles", {
        path: "/review-cycles",
        page: "OrganizationReviewCycles",
        access: withFeature("PerformanceReviews", PERFORMANCE_CYCLE_DETAILS_ACCESS),
        lazy: async () => {
            const { OrganizationReviewCyclesPage } = await import("./pages/review-cycles/list/OrganizationReviewCyclesPage.tsx");

            return { element: <OrganizationReviewCyclesPage /> };
        }
    });

    const feedback = mainPage.registerRoute("feedback", {
        path: "/feedback",
        page: "OrganizationFeedback",
        access: withFeature("Engagement", ORGANIZATION_FEEDBACK_INBOX_ACCESS),
        lazy: async () => {
            const { OrganizationFeedbackPage } = await import("./pages/feedback/OrganizationFeedbackPage.tsx");

            return { element: <OrganizationFeedbackPage /> };
        }
    });

    runtime.registerRoute({ path: "/org/feedback/:feedbackId", element: null }, { parentPath: "/org/feedback" });

    const orgChart = mainPage.registerRoute("org-chart", {
        path: "org-chart/default",
        page: "OrgChart",
        onRenderError: reportOrgChartRenderFailed,
        fullHeight: true,
        topNavVariant: "solid",
        access: ORG_CHART_ACCESS,
        lazy: async () => {
            const { OrgChartPage } = await import("./pages/org-chart/OrgChartPage.tsx");

            return { element: <OrgChartPage /> };
        }
    });

    orgChart.registerOverlay("member", {
        segment: "members/:memberId",
        page: "OrgChartMember",
        lazy: async () => {
            const { OrgChartMemberSidebarRoute } = await import("./pages/org-chart/OrgChartMemberSidebarRoute.tsx");

            return { element: <OrgChartMemberSidebarRoute /> };
        }
    });

    orgChart.registerOverlay("open-role", {
        segment: "open-roles/:openRoleId",
        page: "OrgChartOpenRole",
        lazy: async () => {
            const { OrgChartOpenRoleSidebarRoute } = await import("./pages/org-chart/OrgChartOpenRoleSidebarRoute.tsx");

            return { element: <OrgChartOpenRoleSidebarRoute /> };
        }
    });

    orgChart.registerOverlay("edit", {
        segment: "edit",
        page: "OrgChartEdit",
        access: ORGANIZATION_ORG_CHART_EDIT_ACCESS,
        lazy: async () => {
            const { OrgChartEditRoute } = await import("./pages/org-chart/OrgChartEditRoute.tsx");

            return { element: <OrgChartEditRoute /> };
        }
    });

    area.registerOverlay("org-chart-entry", {
        segment: "org-chart",
        lazy: async () => {
            const [{ Navigate }, { ORG_CHART_PATH }] = await Promise.all([import("react-router"), import("./pages/org-chart/orgChartPaths.ts")]);

            return { element: <Navigate to={ORG_CHART_PATH} replace /> };
        }
    });

    orgChart.registerOverlay("open-roles", {
        segment: "open-roles",
        page: "OrgChart",
        lazy: async () => {
            const [{ Navigate }, { ORG_CHART_PATH }] = await Promise.all([import("react-router"), import("./pages/org-chart/orgChartPaths.ts")]);

            return { element: <Navigate to={ORG_CHART_PATH} replace /> };
        }
    });

    const { meetings, actionItems, engagement, progression } = registerProfileRoutes(runtime, i18nextInstance);
    registerReviewRoutes(runtime, i18nextInstance, PEOPLE_REVIEW_PLACEMENT);
    registerReviewRoutes(runtime, i18nextInstance, { kind: "cycle", origin: "settings" });
    registerReviewRoutes(runtime, i18nextInstance, { kind: "cycle", origin: "home" });
    registerProgressionReviewsRoutes(runtime, i18nextInstance);
    registerMeetingsRoutes(runtime, i18nextInstance);
    registerGoalsRoutes(runtime, i18nextInstance);
    registerGoalEditRoutes(runtime, i18nextInstance);
    registerProgressionGoalsRoutes(runtime, i18nextInstance);
    registerMemberPlansRoutes(runtime, i18nextInstance);
    registerEngagementFeedbackRoutes(runtime, i18nextInstance);
    registerEngagementMetricsRoutes(runtime, i18nextInstance);
    registerOrganizationGoalRoutes(runtime, i18nextInstance);

    registerArea(runtime, "goal-new", "/goals/new", FlagIcon, i18nextInstance, { access: withFeature("Goals") }).registerPage({
        page: "GoalCreate",
        lazy: async () => {
            const { NewGoalPage } = await import("./pages/goals/NewGoalPage.tsx");

            return { element: <NewGoalPage /> };
        }
    });

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        const hasGoals = hasFeature("Goals")(session);
        const hasReviews = hasFeature("PerformanceReviews")(session);

        if (hasProduct(session, "wpm") && hasGoals) {
            registerCreateMenuItem(deferredRuntime, {
                id: "goal",
                label: { instance: i18nextInstance, namespace: "CreateMenu", resourceKey: "newGoal" },
                icon: FlagIcon,
                to: "/goals/new",
                priority: 60
            });
        }

        engagement.registerLink({ priority: 993 });
        progression.registerLink({ priority: 992 });

        const isMeetingsEnabled = hasFeature("Meetings")(session);

        if (isMeetingsEnabled) {
            meetings.registerLink({ priority: 991 });
        }

        actionItems.registerLink({ priority: 990 });

        overview.registerLink({ priority: 1000, icon: RectangleGroupIcon, commandMenu: true });
        people.registerLink({ priority: 999, icon: UsersIcon, commandMenu: true });

        if (session?.memberId && hasProduct(session, "wov") && hasFeature("Recognition")(session)) {
            goodVibes.registerLink({ priority: 998, icon: HeartIcon, commandMenu: true, sidebarGroup: "engagement" });
        }

        if (hasGoals) {
            goals.registerLink({ priority: 997, icon: FlagIcon, commandMenu: true, sidebarGroup: "progression" });
        }

        if (deferredRuntime.getFeatureFlag("plans", false) && hasAnyRole(session, ORGANIZATION_PLANS_ACCESS) && hasFeature("Plans")(session)) {
            plans.registerLink({ priority: 995, icon: ClipboardDocumentListIcon, commandMenu: true, limitedVisibility: true, sidebarGroup: "progression" });
        }

        if (hasAnyRole(session, PERFORMANCE_CYCLE_DETAILS_ACCESS) && hasReviews) {
            reviewCycles.registerLink({ priority: 996, icon: ClipboardDocumentListIcon, commandMenu: true, limitedVisibility: true, sidebarGroup: "progression" });
        }

        if (hasAnyRole(session, ORGANIZATION_FEEDBACK_INBOX_ACCESS) && hasProduct(session, "wov") && hasFeature("Engagement")(session)) {
            feedback.registerLink({ priority: 997, icon: ChatBubbleLeftIcon, commandMenu: true, limitedVisibility: true, sidebarGroup: "engagement" });
        }

        area.registerToTopNav({ priority: 125 });

        if (deferredRuntime.getFeatureFlag("org-chart-viewer", false) && hasAnyRole(session, ORG_CHART_ACCESS)) {
            orgChart.registerLink({ priority: 997, icon: ShareIcon });
        }

        if (deferredRuntime.getFeatureFlag("cmp-classic", false) && hasFeature("Compensation")(session)) {
            // oxlint-disable-next-line typescript/restrict-template-expressions -- Squide 19.2.1's class d.ts types getEnvironmentVariable() as never; the registry holds a string
            mainPage.registerExternalLink("compensation", { to: `${deferredRuntime.getEnvironmentVariable("compensationUrl")}`, priority: 990, sidebarGroup: "compensation" });
        }

        if (session?.memberId) {
            const hasPerformance = hasProduct(session, "wpm");
            const availability = { hasGoals: hasPerformance && hasGoals, hasReviews: hasPerformance && hasReviews, hasEngagement: hasProduct(session, "wov") && hasFeature("Engagement")(session), hasMeetings: isMeetingsEnabled };

            for (const link of meShortcutLinks(session.memberId, availability, i18nextInstance)) {
                getCommandMenuLinksRegistryPlugin(deferredRuntime)?.registerLink(link);
            }
        }
    };
};
